import { AHText, Box, ListItem } from "arkh";
import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { WorkspaceRoutineOccasionsHistoryQuery } from "./__generated__/WorkspaceRoutineOccasionsHistoryQuery.graphql";
import { WorkspaceRoutineOccasionsHistoryList_Routine$key } from "./__generated__/WorkspaceRoutineOccasionsHistoryList_Routine.graphql";
import { FormattedMessage } from "react-intl";
import { RoutineOccasionHistoryListComponent } from "../../../RoutineOccasion/RoutineOccasionHistoryListComponent";

import { PillIndicator } from "../../PillIndicator";
import { FormattedDateRawInWorkspace } from "../../../../common/dates/useFormattedDateRaw";
import { RoutineOccasionHistoryModalContainer } from "@/src/features/RoutineOccasion/RoutineOccassionHistoryContainer";

function WorkspaceRoutineOccasionsHistoryList(props: {
  routine: WorkspaceRoutineOccasionsHistoryList_Routine$key;
  routineId: string;
  onSelectOccasion: (sequenceNumber: number) => void;
  selectedSequenceNumber: number;
  onClose: () => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceRoutineOccasionsHistoryList_Routine on Routine
      @refetchable(
        queryName: "WorkspaceRoutineOccasionsHistoryListRefetchQuery"
      ) {
        id
        workspaceOccasions(
          first: $first
          after: $after
          workspaceId: $workspaceId
        )
          @connection(
            key: "WorkspaceRoutineOccasionsHistoryList_workspaceOccasions"
          ) {
          edges {
            node {
              id
              startAt
              sequenceNumber
              storesCompletedCount
              storesCount
              issueCount
            }
          }
        }
      }
    `,
    props.routine
  );

  const occasions = useMemo(
    () => data?.workspaceOccasions?.edges.map((i) => i.node) ?? [],
    [data?.workspaceOccasions?.edges]
  );

  const renderItem = useCallback(
    ({ item }: { item: (typeof occasions)[number] }) => {
      const percentage = (item.storesCompletedCount / item.storesCount) * 100;
      return (
        <ListItem
          onPress={() => props.onSelectOccasion(item.sequenceNumber)}
          minHeight={75}
          right={
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <AHText spaceRight="medium">
                <FormattedMessage
                  defaultMessage="{completedStores}/{totalStores} stores"
                  description="WorkspaceRoutineOccasiosHistory stores"
                  values={{
                    completedStores: item.storesCompletedCount,
                    totalStores: item.storesCount,
                  }}
                />
              </AHText>
              <Box
                style={{
                  marginRight: 12,
                }}
              >
                <PillIndicator percentage={percentage} />
                {item.issueCount > 0 ? (
                  <Box
                    style={{
                      position: "absolute",
                      top: -14,
                      right: -12,
                      width: 24,
                      height: 18,
                      borderRadius: 10,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    color="negative"
                  >
                    <AHText color="text-inverted" type="subtitle">
                      {item.issueCount}
                    </AHText>
                  </Box>
                ) : null}
              </Box>
            </Box>
          }
        >
          <ListItem.Title>
            <FormattedDateRawInWorkspace dateISO={item.startAt} />
          </ListItem.Title>
        </ListItem>
      );
    },
    [props]
  );

  return (
    <RoutineOccasionHistoryListComponent
      getItemLayout={(_, index) => ({
        length: 75 + 1,
        offset: (75 + 1) * index + 15,
        index,
      })}
      onClose={props.onClose}
      hasNext={hasNext}
      isLoadingNext={isLoadingNext}
      loadNext={loadNext}
      occasions={occasions}
      renderItem={renderItem}
    />
  );
}

function WorkspaceRoutineOccasionsHistoryComponent(props: {
  fetchKey: string;
  routineId: string;
  selectedSequenceNumber: number;
  workspaceId: string;
  archivedRoutine: boolean;
  onClose: () => void;
  onSelectOccasion: (sequenceNumber: number) => void;
}) {
  const data = useLazyLoadQuery<WorkspaceRoutineOccasionsHistoryQuery>(
    graphql`
      query WorkspaceRoutineOccasionsHistoryQuery(
        $routineId: ID!
        $first: Int
        $after: String
        $workspaceId: ID!
      ) {
        routine(id: $routineId) {
          id
          ...WorkspaceRoutineOccasionsHistoryList_Routine
        }
      }
    `,
    {
      routineId: props.routineId,
      after: props.archivedRoutine
        ? undefined
        : props.selectedSequenceNumber + 1 + "",
      first: 10,
      workspaceId: props.workspaceId,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  if (!data.routine) {
    return null;
  }
  return (
    <WorkspaceRoutineOccasionsHistoryList
      onClose={props.onClose}
      onSelectOccasion={props.onSelectOccasion}
      routine={data.routine}
      routineId={props.routineId}
      selectedSequenceNumber={props.selectedSequenceNumber}
    />
  );
}

function InnerWorkspaceRoutineOccasionsHistory(props: {
  routineId: string;
  sequenceNumber: number;
  onSelectOccasion: (sequenceNumber: number) => void;
  workspaceId: string;
  archivedRoutine: boolean;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <RoutineOccasionHistoryModalContainer
      isOpen={props.isOpen}
      onSelectOccasion={props.onSelectOccasion}
      onClose={props.onClose}
    >
      {({ onCloseOccasion, selectSequence }) => (
        <LazyQueryNetworkBoundrary>
          {(fetchKey) => (
            <WorkspaceRoutineOccasionsHistoryComponent
              workspaceId={props.workspaceId}
              onSelectOccasion={selectSequence}
              routineId={props.routineId}
              onClose={onCloseOccasion}
              selectedSequenceNumber={props.sequenceNumber}
              fetchKey={fetchKey}
              archivedRoutine={props.archivedRoutine}
            />
          )}
        </LazyQueryNetworkBoundrary>
      )}
    </RoutineOccasionHistoryModalContainer>
  );
}

export const WorkspaceRoutineOccasionsHistory = React.memo(
  InnerWorkspaceRoutineOccasionsHistory
);
